import './App.css';
import {
  BrowserRouter as Router, Routes, Route, Navigate, useLocation,
} from 'react-router-dom';
import { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga4';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import Navbar from './components/Navbar';
import AnnouncementModal from './components/AnnouncementModal';
import { useAnnouncement } from './hooks/useAnnouncement';
import Footer from './components/Footer';
import { startProgress, doneProgress } from './utils/nprogress';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CreatePuzzle from './pages/CreatePuzzle';
import PlayPuzzle from './pages/PlayPuzzle';
import DailyPuzzle from './pages/DailyPuzzle';
import MyPuzzles from './pages/MyPuzzles';
import Feed from './pages/Feed';
import Profile from './pages/Profile';
import Account from './pages/Account';
import ImageLibrary from './pages/admin/ImageLibrary';
import DailyPuzzleManager from './pages/admin/DailyPuzzleManager';
import StatsDashboard from './pages/admin/StatsDashboard';
import FeedbackManager from './pages/admin/FeedbackManager';
import NotFound from './pages/NotFound';
import About from './pages/About';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Leaderboard from './pages/Leaderboard';
import JoinRoom from './pages/JoinRoom';
import MyRooms from './pages/MyRooms';
import MyStats from './pages/MyStats';
import HowItWorks from './pages/HowItWorks';
import WhatsNew from './pages/WhatsNew';
import League from './pages/League';
import SearchUsers from './pages/SearchUsers';
import Friends from './pages/Friends';
import TestPuzzle from './pages/TestPuzzle';
import FeedbackButton from './components/FeedbackButton';
import AutoReload from './components/AutoReload';

// Initialize Google Analytics
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
if (GA_MEASUREMENT_ID) {
  ReactGA.initialize(GA_MEASUREMENT_ID);
}

// Analytics wrapper to track page views and scroll to top on route change
function AnalyticsTracker({ children }) {
  const location = useLocation();

  useEffect(() => {
    // Start progress bar with random brand color
    startProgress();

    // Scroll to top on route change
    window.scrollTo(0, 0);

    // Track page view
    if (GA_MEASUREMENT_ID) {
      ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
    }

    // Complete progress bar after short delay
    const timer = setTimeout(() => doneProgress(), 300);
    return () => clearTimeout(timer);
  }, [location]);

  return children;
}

AnalyticsTracker.propTypes = {
  children: PropTypes.node.isRequired,
};

// Layout wrapper for pages that use the standard Navbar/Footer
function StandardLayout({ children }) {
  return (
    <div className="App min-h-screen bg-puzzle-background">
      <Navbar />
      {children}
      <Footer />
    </div>
  );
}

StandardLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

// Protected route wrapper - redirects guests to login
function ProtectedRoute({ children }) {
  const { user, loading } = useContext(AuthContext);

  // Show nothing while checking auth status
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

// Admin route wrapper - redirects non-admins to home
function AdminRoute({ children }) {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
}

AdminRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

// Announcement wrapper to show feature announcements
function AnnouncementWrapper() {
  const { announcement, dismissAnnouncement } = useAnnouncement();
  return <AnnouncementModal announcement={announcement} onDismiss={dismissAnnouncement} />;
}

// Theme applier to set dark/light mode on document root
function ThemeApplier() {
  const { theme } = useSettings();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return null;
}

function App() {
  return (
    <Router>
      <AnalyticsTracker>
        <AuthProvider>
          <SettingsProvider>
            <ThemeApplier />
            <AutoReload />
            <AnnouncementWrapper />
            <FeedbackButton />
            <Routes>
              {/* Feed has its own layout (no Navbar/Footer) */}
              <Route path="/feed" element={<Feed />} />
              <Route
                path="/profile/:username"
                element={(
                  <StandardLayout>
                    <Profile />
                  </StandardLayout>
                )}
              />

              {/* Standard layout pages */}
              <Route
                path="/"
                element={(
                  <StandardLayout>
                    <DailyPuzzle />
                  </StandardLayout>
                )}
              />
              <Route
                path="/create"
                element={(
                  <StandardLayout>
                    <CreatePuzzle />
                  </StandardLayout>
                )}
              />
              <Route
                path="/leaderboard"
                element={(
                  <StandardLayout>
                    <Leaderboard />
                  </StandardLayout>
                )}
              />

              <Route
                path="/league"
                element={(
                  <ProtectedRoute>
                    <StandardLayout>
                      <League />
                    </StandardLayout>
                  </ProtectedRoute>
                )}
              />

              <Route
                path="/search"
                element={(
                  <ProtectedRoute>
                    <StandardLayout>
                      <SearchUsers />
                    </StandardLayout>
                  </ProtectedRoute>
                )}
              />
              <Route
                path="/friends"
                element={(
                  <ProtectedRoute>
                    <StandardLayout>
                      <Friends />
                    </StandardLayout>
                  </ProtectedRoute>
                )}
              />

              {/* Admin routes */}
              <Route
                path="/admin/images"
                element={(
                  <AdminRoute>
                    <StandardLayout>
                      <ImageLibrary />
                    </StandardLayout>
                  </AdminRoute>
                )}
              />
              <Route
                path="/admin/daily-puzzles"
                element={(
                  <AdminRoute>
                    <StandardLayout>
                      <DailyPuzzleManager />
                    </StandardLayout>
                  </AdminRoute>
                )}
              />
              <Route
                path="/admin/stats"
                element={(
                  <AdminRoute>
                    <StandardLayout>
                      <StatsDashboard />
                    </StandardLayout>
                  </AdminRoute>
                )}
              />
              <Route
                path="/admin/feedback"
                element={(
                  <AdminRoute>
                    <StandardLayout>
                      <FeedbackManager />
                    </StandardLayout>
                  </AdminRoute>
                )}
              />
              <Route
                path="/play/:id"
                element={(
                  <StandardLayout>
                    <PlayPuzzle />
                  </StandardLayout>
                )}
              />
              <Route
                path="/my-puzzles"
                element={(
                  <ProtectedRoute>
                    <StandardLayout>
                      <MyPuzzles />
                    </StandardLayout>
                  </ProtectedRoute>
                )}
              />
              <Route
                path="/login"
                element={(
                  <StandardLayout>
                    <Login />
                  </StandardLayout>
                )}
              />
              <Route
                path="/register"
                element={(
                  <StandardLayout>
                    <Register />
                  </StandardLayout>
                )}
              />
              <Route
                path="/forgot-password"
                element={(
                  <StandardLayout>
                    <ForgotPassword />
                  </StandardLayout>
                )}
              />
              <Route
                path="/reset-password/:token"
                element={(
                  <StandardLayout>
                    <ResetPassword />
                  </StandardLayout>
                )}
              />
              <Route
                path="/about"
                element={(
                  <StandardLayout>
                    <About />
                  </StandardLayout>
                )}
              />
              <Route
                path="/terms"
                element={(
                  <StandardLayout>
                    <Terms />
                  </StandardLayout>
                )}
              />
              <Route
                path="/privacy"
                element={(
                  <StandardLayout>
                    <Privacy />
                  </StandardLayout>
                )}
              />
              <Route
                path="/how-it-works"
                element={(
                  <StandardLayout>
                    <HowItWorks />
                  </StandardLayout>
                )}
              />
              <Route
                path="/whats-new"
                element={(
                  <StandardLayout>
                    <WhatsNew />
                  </StandardLayout>
                )}
              />
              <Route
                path="/account"
                element={(
                  <ProtectedRoute>
                    <StandardLayout>
                      <Account />
                    </StandardLayout>
                  </ProtectedRoute>
                )}
              />

              {/* Room routes */}
              <Route
                path="/room/:code"
                element={(
                  <StandardLayout>
                    <JoinRoom />
                  </StandardLayout>
                )}
              />
              <Route
                path="/my-rooms"
                element={(
                  <ProtectedRoute>
                    <StandardLayout>
                      <MyRooms />
                    </StandardLayout>
                  </ProtectedRoute>
                )}
              />
              <Route
                path="/my-stats"
                element={(
                  <ProtectedRoute>
                    <StandardLayout>
                      <MyStats />
                    </StandardLayout>
                  </ProtectedRoute>
                )}
              />

              {/* Test puzzle (proof of concept) */}
              <Route
                path="/test-puzzle"
                element={(
                  <StandardLayout>
                    <TestPuzzle />
                  </StandardLayout>
                )}
              />

              {/* 404 - Must be last */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </SettingsProvider>
        </AuthProvider>
      </AnalyticsTracker>
    </Router>
  );
}

export default App;
